/**
 * Data layer for `auto-alert-affiliate-via-email-in-app`. Thin typed fetch wrappers over
 * /api/v1/auto-alert-affiliate-via-email-in-app. Raw `fetch()` is confined to this file
 * per the dash contract (D6).
 */

export type AlertKind =
	| "approval"
	| "rejection"
	| "commission"
	| "payout"
	| "offer_assigned"
	| "offer_paused"
	| "tier_change"
	| "message";

export type AlertChannel = "email" | "in_app";

export interface AffiliateAlertRow {
	id: string;
	event_id: string;
	affiliate_id: string;
	kind: AlertKind;
	channels: AlertChannel[];
	subject: string | null;
	body: string | null;
	delivered_at: number | null;
	created_at: number;
}

export interface ListAffiliateAlertsResponse {
	items: AffiliateAlertRow[];
}

export interface CreateAffiliateAlertBody {
	event_id: string;
	affiliate_id: string;
	kind: AlertKind;
	channels: AlertChannel[];
	subject?: string;
	body?: string;
}

export interface CreateAffiliateAlertResponse {
	ok: true;
	event_id: string;
	created_at: number;
}

export interface MarkDeliveredResponse {
	ok: true;
	delivered_at: number;
	already: boolean;
}

const BASE = "/api/v1/auto-alert-affiliate-via-email-in-app";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listAlerts(params?: {
	affiliate_id?: string;
	limit?: number;
}): Promise<ListAffiliateAlertsResponse> {
	const qs = new URLSearchParams();
	if (params?.affiliate_id) qs.set("affiliate_id", params.affiliate_id);
	if (params?.limit) qs.set("limit", String(params.limit));
	const suffix = qs.toString() ? `?${qs.toString()}` : "";
	const res = await fetch(`${BASE}${suffix}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAffiliateAlertsResponse>(res);
}

export async function createAlert(
	body: CreateAffiliateAlertBody,
): Promise<CreateAffiliateAlertResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateAffiliateAlertResponse>(res);
}

export async function markDelivered(
	event_id: string,
): Promise<MarkDeliveredResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(event_id)}/delivered`,
		{
			method: "POST",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<MarkDeliveredResponse>(res);
}

export const affiliateAlertQueryKeys = {
	all: ["auto-alert-affiliate-via-email-in-app"] as const,
	list: (params?: { affiliate_id?: string }) =>
		[
			...affiliateAlertQueryKeys.all,
			"list",
			params?.affiliate_id ?? "all",
		] as const,
	detail: (event_id: string) =>
		[...affiliateAlertQueryKeys.all, "detail", event_id] as const,
};
